import { z } from 'zod';
import { SUPPORTED_LOCALES } from '../constants/app.js';
import { idSchema, moneySchema } from './common.js';
import { paginationMetaSchema } from './pagination.js';
import { journalLineInputSchema, journalSchema, ledgerDateSchema } from './financial-accounting.js';
import { compareDecimal, subtractDecimal } from '../utils/decimal.js';
export const ledgerClosingQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    q: z.string().trim().max(160).default(''),
    asOf: ledgerDateSchema.optional(),
    locale: z.enum(SUPPORTED_LOCALES).default('de'),
});
const key = z.string().min(8).max(160), version = z.number().int().positive();
const positive = moneySchema.refine((v) => compareDecimal(v, '0') > 0, 'ledgerClosing.errors.positive');
const nonnegative = moneySchema.refine((v) => compareDecimal(v, '0') >= 0, 'ledgerClosing.errors.positive');
export const closingCommandSchema = z.object({ idempotencyKey: key });
export const templateDefinitionSchema = z.object({
    description: z.string().trim().min(3).max(240),
    reference: z.string().trim().min(1).max(160),
    lines: z.array(journalLineInputSchema).min(2).max(500),
    startDate: ledgerDateSchema,
    intervalMonths: z.number().int().min(1).max(120).default(1),
    reversalDate: ledgerDateSchema.nullable().default(null),
});
export const journalTemplateCreateSchema = z.object({
    name: z.string().trim().min(1).max(160),
    kind: z.enum(['JOURNAL', 'RECURRING', 'ACCRUAL', 'PROVISION']),
    definition: templateDefinitionSchema,
    idempotencyKey: key,
});
export const journalTemplateUpdateSchema = journalTemplateCreateSchema.extend({
    version,
    active: z.boolean(),
});
export const journalTemplateSchema = journalTemplateCreateSchema
    .omit({ idempotencyKey: true })
    .extend({ id: idSchema, version, active: z.boolean() });
export const journalTemplateListSchema = z.object({
    meta: paginationMetaSchema,
    data: z.array(journalTemplateSchema),
});
export const journalTemplateRunSchema = z.object({
    version,
    through: ledgerDateSchema,
    idempotencyKey: key,
});
export const journalTemplateCopySchema = z.object({
    name: z.string().trim().min(1).max(160),
    version,
    idempotencyKey: key,
});
export const journalTemplateHistorySchema = z.object({
    data: z.array(journalTemplateSchema.extend({ createdAt: z.string() })),
});
export const journalTemplateRunResultSchema = z.object({ data: z.array(journalSchema) });
export const assetCreateSchema = z
    .object({
    code: z.string().trim().min(1).max(80),
    name: z.string().trim().min(1).max(160),
    acquiredOn: ledgerDateSchema,
    inServiceOn: ledgerDateSchema,
    cost: positive,
    residual: nonnegative,
    openingDepreciation: nonnegative.default('0'),
    usefulLifeMonths: z.number().int().min(1).max(1200),
    assetAccountId: idSchema,
    expenseAccountId: idSchema,
    accumulatedAccountId: idSchema,
    acquisitionJournalId: idSchema,
    openingEvidence: z.string().trim().min(10).max(2000).nullable().default(null),
    idempotencyKey: key,
})
    .superRefine((v, ctx) => {
    if (compareDecimal(v.openingDepreciation, subtractDecimal(v.cost, v.residual, 4)) > 0)
        ctx.addIssue({
            code: 'custom',
            path: ['openingDepreciation'],
            message: 'ledgerClosing.errors.acquisition',
        });
    if (v.inServiceOn < v.acquiredOn)
        ctx.addIssue({ code: 'custom', path: ['inServiceOn'], message: 'ledgerClosing.errors.date' });
    if (compareDecimal(v.residual, v.cost) >= 0)
        ctx.addIssue({
            code: 'custom',
            path: ['residual'],
            message: 'ledgerClosing.errors.residual',
        });
    if (v.assetAccountId === v.accumulatedAccountId)
        ctx.addIssue({
            code: 'custom',
            path: ['accumulatedAccountId'],
            message: 'ledgerClosing.errors.accounts',
        });
});
export const assetEventSchema = z.object({
    id: idSchema,
    kind: z.enum(['OPENING', 'DEPRECIATION', 'DISPOSAL', 'CORRECTION']),
    effectiveDate: ledgerDateSchema,
    journalId: idSchema,
    status: z.enum(['DRAFT', 'POSTED']),
    amount: moneySchema,
    reversalJournalId: idSchema.nullable(),
    withdrawn: z.boolean().default(false),
});
export const assetSchema = z.object({
    id: idSchema,
    code: z.string(),
    name: z.string(),
    acquiredOn: ledgerDateSchema,
    inServiceOn: ledgerDateSchema,
    cost: moneySchema,
    residual: moneySchema,
    openingDepreciation: moneySchema.default('0.0000'),
    usefulLifeMonths: z.number().int(),
    assetAccountId: idSchema,
    expenseAccountId: idSchema,
    accumulatedAccountId: idSchema,
    acquisitionJournalId: idSchema,
    openingEvidence: z.string().nullable(),
    version,
    events: z.array(assetEventSchema),
    schedule: z.array(z.object({ date: ledgerDateSchema, amount: moneySchema, carryingAmount: moneySchema })),
    currentCost: moneySchema,
    asOf: ledgerDateSchema.nullable(),
    costAdjustments: z.array(z.object({
        id: idSchema,
        kind: z.enum(['REDUCTION', 'REVERSAL']),
        amount: moneySchema,
        effectiveDate: ledgerDateSchema,
        journalId: idSchema,
        vendorInvoiceId: idSchema,
        sourcePosition: z.number().int().positive(),
        creditPosition: z.number().int().positive(),
        reversalOfId: idSchema.nullable(),
    })),
    carryingAmount: moneySchema,
    disposed: z.boolean(),
});
export const assetListSchema = z.object({ meta: paginationMetaSchema, data: z.array(assetSchema) });
export const assetDepreciationSchema = z.object({ through: ledgerDateSchema, idempotencyKey: key });
export const assetPostSchema = z.object({ eventId: idSchema, version, idempotencyKey: key });
export const assetDisposalSchema = z.object({
    effectiveDate: ledgerDateSchema,
    lossAccountId: idSchema,
    proceedsInvoiceId: idSchema.nullable().default(null),
    reason: z.string().trim().min(10).max(2000),
    idempotencyKey: key,
});
export const assetCorrectionSchema = z.object({
    eventId: idSchema,
    effectiveDate: ledgerDateSchema,
    reason: z.string().trim().min(10).max(2000),
    idempotencyKey: key,
});
export const closingPreviewRequestSchema = z.object({
    yearId: idSchema,
    nextYearId: idSchema,
    equityAccountId: idSchema,
});
export const closingPreviewSchema = z.object({
    yearId: idSchema,
    nextYearId: idSchema,
    equityAccountId: idSchema,
    checksum: z.string().length(64),
    canClose: z.boolean(),
    findings: z.array(z.object({ code: z.string(), count: z.number().int() })),
    profit: moneySchema,
    close: z.array(z.object({ accountId: idSchema, balance: moneySchema })),
    open: z.array(z.object({ accountId: idSchema, balance: moneySchema })),
});
export const closingExecuteSchema = closingPreviewRequestSchema.extend({
    checksum: z.string().length(64),
    idempotencyKey: key,
    confirmed: z.literal(true),
});
export const closingRunSchema = z.object({
    id: idSchema,
    yearId: idSchema,
    nextYearId: idSchema,
    version,
    state: z.enum(['CLOSED', 'REOPENED']),
    closingJournalId: idSchema.nullable(),
    openingJournalId: idSchema.nullable(),
    reopenedAt: z.string().nullable(),
    reopenReason: z.string().nullable(),
});
export const closingRunListSchema = z.object({
    meta: paginationMetaSchema,
    data: z.array(closingRunSchema),
});
export const closingReopenSchema = z.object({
    version,
    reason: z.string().trim().min(10).max(2000),
    idempotencyKey: key,
});
export const fxValuationCreateSchema = z
    .object({
    invoiceId: idSchema,
    effectiveDate: ledgerDateSchema,
    rate: z
        .string()
        .regex(/^\d+(\.\d{1,10})?$/u)
        .refine((v) => compareDecimal(v, '0') > 0),
    rateDate: ledgerDateSchema,
    rateSource: z.string().trim().min(3).max(240),
    shortTerm: z.boolean(),
    fxAccountId: idSchema,
    reversalDate: ledgerDateSchema,
    idempotencyKey: key,
})
    .superRefine((v, ctx) => {
    if (v.rateDate !== v.effectiveDate)
        ctx.addIssue({
            code: 'custom',
            path: ['rateDate'],
            message: 'ledgerClosing.errors.rateDate',
        });
    if (v.reversalDate <= v.effectiveDate)
        ctx.addIssue({
            code: 'custom',
            path: ['reversalDate'],
            message: 'ledgerClosing.errors.date',
        });
});
export const fxValuationSchema = z.object({
    id: idSchema,
    invoiceId: idSchema,
    effectiveDate: ledgerDateSchema,
    rate: z.string(),
    rateDate: ledgerDateSchema,
    rateSource: z.string(),
    amount: moneySchema,
    journalId: idSchema,
    reversalJournalId: idSchema,
    reversalDate: ledgerDateSchema,
});
export const fxValuationListSchema = z.object({
    meta: paginationMetaSchema,
    data: z.array(fxValuationSchema),
});
export const fxValuationPreviewSchema = z.object({
    invoiceId: idSchema,
    openAmount: moneySchema,
    carryingAmount: moneySchema,
    amount: moneySchema,
    effectiveDate: ledgerDateSchema,
    reversalDate: ledgerDateSchema,
});
export const fxValuationResultSchema = z.object({
    data: fxValuationSchema.nullable(),
    amount: moneySchema,
    result: z.enum(['POSTED', 'NO_CHANGE']),
});
export const fxValuationPostSchema = fxValuationCreateSchema.and(z.object({ confirmed: z.literal(true) }));
export const ledgerClosingEditorSchema = z.record(z.string());
export const assetCreditSourceSchema = z.object({
    sourceInvoiceId: idSchema,
    assets: z.array(z.object({
        id: idSchema,
        code: z.string(),
        name: z.string(),
        cost: moneySchema,
        currentCost: moneySchema,
        assetAccountId: idSchema,
        residual: moneySchema,
        sourceAllocations: z.array(z.object({ sourcePosition: z.number().int().positive(), amount: moneySchema })),
        dependencies: z.array(z.object({
            eventId: idSchema,
            kind: z.string(),
            effectiveDate: ledgerDateSchema,
            journalId: idSchema,
        })),
    })),
    rows: z.array(z.object({
        sourcePosition: z.number().int().positive(),
        accountId: idSchema,
        accountNumber: z.string(),
        accountName: z.string(),
        homeCost: moneySchema,
        rate: moneySchema,
        inputTaxDeductionPercent: moneySchema,
        costCenterId: idSchema.nullable(),
        costObjectId: idSchema.nullable(),
    })),
});
export const assetDraftWithdrawalEvidenceSchema = z
    .object({
    correctionKind: z.literal('DRAFT_WITHDRAWAL'),
    withdrawnDraftEventId: idSchema,
    originalJournalId: idSchema,
    actorId: idSchema,
    reason: z.string().trim().min(10).max(2000),
    effectiveDate: ledgerDateSchema,
})
    .strict();
//# sourceMappingURL=ledger-closing.js.map